
import tools from '../../tools.js';
import LicenseInput from './license-input';
import TimeInput from './time-input';
import TimeInputNative from './time-input-native';
import FileInput from './file-input';

/**
 * Form V10 handling
 *
 * Field automatic validation can be disabled by using .form-v10--no-validation class if needed.
 */
class FormV10 {

  /**
   * Constructor
   * @param {HTMLElement} formEl element to bind to
   */
  constructor( formEl ) {
    this._form = formEl;

    this._formErrors = false;

    this._initLicenseInput();
    this._initTimeInput();
    this._initPhoneInput();
    this._initFileInputs();
    this._initValidation();
    this._handleFormSubmit();
  }

  /**
   * License input creation and handling
   */
  _initLicenseInput() {
    const licenseInputs = this._form.querySelectorAll('input.license-input');
    licenseInputs.forEach(licenseInputElement => new LicenseInput(licenseInputElement));
  }

  /**
   * Time input creation and handling
   */
  _initTimeInput() {
    const timeInputElements = this._form.querySelectorAll('input.time-input:not([type=time])');
    timeInputElements.forEach( timeInputElement => new TimeInput( timeInputElement ) );

    const timeInputNativeElements = this._form.querySelectorAll('input[type=time]');
    timeInputNativeElements.forEach( timeInputNativeElement => new TimeInputNative( timeInputNativeElement ) );
  }

  /**
   * Time input creation and handling
   */
  _initPhoneInput() {
    const phoneInput = this._form.querySelector('input.phone-input');

    if ( phoneInput && phoneInput.dataset.prefix) {
      const prefixElement = document.createElement('div');
      prefixElement.classList.add('phone-prefix');
      prefixElement.innerText = phoneInput.dataset.prefix;
      phoneInput.parentNode.prepend(prefixElement);
    }
  }

  /**
   * File input handling
   */
  _initFileInputs() {
    const fileInputs = this._form.querySelectorAll('input[type=file]');
    fileInputs.forEach(input => new FileInput(input));
  }


  /**
   * Phone input handling
   */
  _initValidation() {
    const fields = this._form.querySelectorAll('.form-v10__field.stage-required');

    fields.forEach(field => {
      const labelElement = field.querySelector('label');

      const input = field.querySelector('[required]') || (labelElement ? field.querySelector(`#${labelElement.getAttribute('for')}`) : null );

      // Live validation for inputs other than radio and checkbox
      if ( input && !['radio', 'checkbox'].includes(input.type) ) {
        input.addEventListener('blur', (event) => {
          this._validateInput(event.currentTarget);
        });
      }
    });
  }

  validateRequired( inputElement, labelElement ) {
    if( !inputElement.value ) {
      const message = labelElement && labelElement.innerText ? `${labelElement.innerText} on pakollinen.` : '';
      return { message };
    }
  }

  validateCheckboxAndRadioInputs( inputElement, formField ) {
    const checked = formField.querySelectorAll('[type=radio], [type=checkbox]')
      .some( element => inputElement.checked );

    if( !checked ) {
      return {
        error: 'Kenttä on pakollinen.'
      }
    }
  }

  /**
   * Validate input field
   * @param {HTMLElement} inputElement input element
   */
  _validateInput(inputElement) {

    const formField = inputElement.closest('.form-v10__field.stage-required');
    const labelElement = formField.querySelector('label');

    let errors = [];

    if( inputElement.value && inputElement.type === 'email' && !tools.validateEmail(inputElement.value) ) {
      errors.push( { message: 'Tarkista sähköpostiosoite.' } );
    } else if( inputElement.value && inputElement.type === 'tel' && !tools.validatePhone(inputElement.value) ) {
      errors.push( { message: 'Puhelinnumeron tulee sisältää vain numeroita.' } );
    } else if ( ['radio', 'checkbox'].includes(inputElement.type) ) {
      const error = this.validateCheckboxAndRadioInputs(inputElement);

      if( error ) {
        formField.classList.add('hide-validation-icon');
      }

      errors.push( error );
    } else {
      errors.push( this.validateRequired( inputElement, labelElement ) );
    }

    //Filter out undefined values from successfull validations
    errors = errors.filter(Boolean);

    let errorMessageElement = formField.querySelector('.form-v10__input-error');

    if (errors.length > 0 ) {
      this._formErrors = errors;

      formField.classList.remove('stage-success');
      formField.classList.add('stage-error');

      const errorMessage = errors.map( error => error.message ).join(' ');

      // Create error message element if it doesn't exist yet
      if (errorMessage) {
        if (!errorMessageElement) {
          errorMessageElement = document.createElement('div');
          errorMessageElement.classList.add('form-v10__input-error', 'overline');
          errorMessageElement.innerText = errorMessage;
          formField.append(errorMessageElement);
        } else {
          errorMessageElement.innerText = errorMessage;
          errorMessageElement.classList.toggle('form-v10__input-error--hidden', false);
        }
      }
    } else {
      formField.classList.remove('stage-error');
      formField.classList.add('stage-success');

      if (errorMessageElement) {
        errorMessageElement.classList.toggle('form-v10__input-error--hidden', true);
      }
    }
  }

  /**
   * Handle form submit
   */
  _handleFormSubmit() {
    this._form.addEventListener('submit', (event) => {
      this._formErrors = false; // Reset errors

      this._form.querySelectorAll('.form-v10__field.stage-required').forEach(field => {
        const labelElement = field.querySelector('label');
        const input = field.querySelector('[required]') || field.querySelector(`#${labelElement.getAttribute('for')}`);
        this._validateInput(input);
      });

      // Prevent submit if errors are found
      if (this._formErrors) {
        event.preventDefault();
      }
    });
  }

  /**
   * Handle field specific validation routine. Show success icon when
   * field's validation is completed or error message if validation fails and
   * return some error message.
   *
   * @param {HTMLElement} formField form field
   * @param {boolean} success is field considered to be valid or not
   * @param {object} error validation error object
   */
  static showFieldValidation(formField, success, error) {

    let errorMessageElement = formField.querySelector('.form-v10__input-error');

    if( success ) {
      formField.classList.add('stage-success');
      formField.classList.remove('stage-error');
      errorMessageElement?.classList.toggle('form-v10__input-error--hidden', true);
    }

    if( error ) {
      if (!errorMessageElement) {
        errorMessageElement = document.createElement('div');
        errorMessageElement.classList.add('form-v10__input-error', 'overline');
        formField.append(errorMessageElement);
      }

      errorMessageElement.innerText = Array.isArray(error) ? error.join(' ') : error;

    }
  }

  /**
   * Initializes form
   */
  static init() {

    const forms = document.querySelectorAll('.form-v10:not(.form-v10--no-validation)');

    forms.forEach( form => {
      new FormV10( form );
    });
  }
}

export default FormV10;
